// filepath: admin-dashboard/src/App.tsx
import { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { MainLayout } from './components/layout/MainLayout';
import { LocaleProvider } from './context/LocaleContext';
import { ProjectProvider } from './context/ProjectContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import ErrorBoundary from './components/ui/ErrorBoundary';

// Auth pages (not lazy — small and critical path)
import LoginPage from './pages/LoginPage';

// Dashboard
const Dashboard = lazy(() => import('./pages/Dashboard'));

// Auth-related pages
const ChangePasswordPage = lazy(() => import('./pages/ChangePasswordPage'));
const UserManagementPage = lazy(() => import('./pages/UserManagementPage'));

// Lazy-loaded service pages
const ServicesList = lazy(() => import('./pages/services/ServicesList'));
const CreateService = lazy(() => import('./pages/services/CreateService'));
const EditService = lazy(() => import('./pages/services/EditService'));

// Lazy-loaded reference pages
const ReferencesList = lazy(() => import('./pages/references/ReferencesList'));
const CreateReference = lazy(
  () => import('./pages/references/CreateReference')
);
const EditReference = lazy(() => import('./pages/references/EditReference'));

// Lazy-loaded location pages
const LocationsList = lazy(() => import('./pages/locations/LocationsList'));
const CreateLocation = lazy(() => import('./pages/locations/CreateLocation'));
const EditLocation = lazy(() => import('./pages/locations/EditLocation'));
const LocationCategories = lazy(
  () => import('./pages/locations/LocationCategories')
);

// Lazy-loaded showreel pages
const ShowreelList = lazy(() => import('./pages/showreel/ShowreelList'));
const CreateShowreel = lazy(() => import('./pages/showreel/CreateShowreel'));
const EditShowreel = lazy(() => import('./pages/showreel/EditShowreel'));

// Lazy-loaded client pages
const ClientsList = lazy(() => import('./pages/clients/ClientsList'));
const CreateClient = lazy(() => import('./pages/clients/CreateClient'));
const EditClient = lazy(() => import('./pages/clients/EditClient'));

// Lazy-loaded HQ pages
const HqItemsList = lazy(() => import('./pages/hq/HqItemsList'));
const CreateHqItem = lazy(() => import('./pages/hq/CreateHqItem'));
const EditHqItem = lazy(() => import('./pages/hq/EditHqItem'));

// Lazy-loaded supporter pages
const SupportersList = lazy(() => import('./pages/supporters/SupportersList'));
const CreateSupporter = lazy(
  () => import('./pages/supporters/CreateSupporter')
);
const EditSupporter = lazy(() => import('./pages/supporters/EditSupporter'));

// Bolt Jr+ pages
const BoltHeroList = lazy(() => import('./pages/boltJr/hero/HeroList'));
const BoltCreateHero = lazy(() => import('./pages/boltJr/hero/CreateHero'));
const BoltEditHero = lazy(() => import('./pages/boltJr/hero/EditHero'));

const BoltKeyFeaturesList = lazy(() => import('./pages/boltJr/keyfeatures/KeyFeaturesList'));
const BoltCreateKeyFeature = lazy(() => import('./pages/boltJr/keyfeatures/CreateKeyFeature'));
const BoltEditKeyFeature = lazy(() => import('./pages/boltJr/keyfeatures/EditKeyFeature'));

const BoltFeaturesList = lazy(() => import('./pages/boltJr/features/BoltFeaturesList'));
const BoltCreateFeature = lazy(() => import('./pages/boltJr/features/CreateBoltFeature'));
const BoltEditFeature = lazy(() => import('./pages/boltJr/features/EditBoltFeature'));

const BoltTrackOptionsList = lazy(() => import('./pages/boltJr/trackoptions/TrackOptionsList'));
const BoltCreateTrackOption = lazy(() => import('./pages/boltJr/trackoptions/CreateTrackOption'));
const BoltEditTrackOption = lazy(() => import('./pages/boltJr/trackoptions/EditTrackOption'));
const BoltTrackOptionTabs = lazy(() => import('./pages/boltJr/trackoptions/TrackOptionTabs'));

const BoltSpecificationsList = lazy(() => import('./pages/boltJr/specifications/SpecificationsList'));
const BoltCreateSpecification = lazy(() => import('./pages/boltJr/specifications/CreateSpecification'));
const BoltEditSpecification = lazy(() => import('./pages/boltJr/specifications/EditSpecification'));

const BoltControllersList = lazy(() => import('./pages/boltJr/controllers/ControllersList'));
const BoltCreateController = lazy(() => import('./pages/boltJr/controllers/CreateController'));
const BoltEditController = lazy(() => import('./pages/boltJr/controllers/EditController'));

const BoltAccessoriesList = lazy(() => import('./pages/boltJr/accessories/AccessoriesList'));
const BoltCreateAccessory = lazy(() => import('./pages/boltJr/accessories/CreateAccessory'));
const BoltEditAccessory = lazy(() => import('./pages/boltJr/accessories/EditAccessory'));

const BoltVideosList = lazy(() => import('./pages/boltJr/videos/BoltVideosList'));
const BoltCreateVideo = lazy(() => import('./pages/boltJr/videos/CreateBoltVideo'));
const BoltEditVideo = lazy(() => import('./pages/boltJr/videos/EditBoltVideo'));

/**
 * Redirect authenticated users away from /login.
 */
const LoginRoute = () => {
  const { isAuthenticated, mustChangePassword } = useAuth();
  if (isAuthenticated) {
    return <Navigate to={mustChangePassword ? '/change-password' : '/'} replace />;
  }
  return <LoginPage />;
};

const SuspenseFallback = (
  <div className="flex h-full items-center justify-center">Loading...</div>
);

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ProjectProvider>
          <LocaleProvider>
            <BrowserRouter>
              <Routes>
                {/* Public login route — no layout */}
                <Route path="/login" element={<LoginRoute />} />

                {/* All protected routes inside MainLayout */}
                <Route
                  path="/*"
                  element={
                    <ProtectedRoute>
                      <MainLayout>
                        <Suspense fallback={SuspenseFallback}>
                          <Routes>
                            {/* Dashboard */}
                            <Route path="/" element={<Dashboard />} />

                            {/* Change password (any authenticated user) */}
                            <Route
                              path="/change-password"
                              element={<ChangePasswordPage />}
                            />

                            {/* User management (admin only) */}
                            <Route
                              path="/users"
                              element={
                                <ProtectedRoute requireAdmin>
                                  <UserManagementPage />
                                </ProtectedRoute>
                              }
                            />

                            {/* === Admiral routes === */}

                            {/* Services routes */}
                            <Route path="/services" element={<ServicesList />} />
                            <Route path="/services/new" element={<CreateService />} />
                            <Route path="/services/:id/edit" element={<EditService />} />

                            {/* References routes */}
                            <Route path="/references" element={<ReferencesList />} />
                            <Route path="/references/new" element={<CreateReference />} />
                            <Route path="/references/:id/edit" element={<EditReference />} />

                            {/* Locations routes */}
                            <Route path="/locations" element={<LocationsList />} />
                            <Route path="/locations/new" element={<CreateLocation />} />
                            <Route path="/locations/:id/edit" element={<EditLocation />} />
                            <Route path="/locations/categories" element={<LocationCategories />} />

                            {/* Showreel routes */}
                            <Route path="/showreel" element={<ShowreelList />} />
                            <Route path="/showreel/new" element={<CreateShowreel />} />
                            <Route path="/showreel/:id/edit" element={<EditShowreel />} />

                            {/* Clients routes */}
                            <Route path="/clients" element={<ClientsList />} />
                            <Route path="/clients/new" element={<CreateClient />} />
                            <Route path="/clients/:id/edit" element={<EditClient />} />

                            {/* HQ routes */}
                            <Route path="/hq-items" element={<HqItemsList />} />
                            <Route path="/hq-items/new" element={<CreateHqItem />} />
                            <Route path="/hq-items/:id/edit" element={<EditHqItem />} />

                            {/* Supporters routes */}
                            <Route path="/supporters" element={<SupportersList />} />
                            <Route path="/supporters/new" element={<CreateSupporter />} />
                            <Route path="/supporters/:id/edit" element={<EditSupporter />} />

                            {/* === Bolt Jr+ routes === */}

                            {/* Hero */}
                            <Route path="/bolt-jr/hero" element={<BoltHeroList />} />
                            <Route path="/bolt-jr/hero/new" element={<BoltCreateHero />} />
                            <Route path="/bolt-jr/hero/:id/edit" element={<BoltEditHero />} />

                            {/* Key Features */}
                            <Route path="/bolt-jr/keyfeatures" element={<BoltKeyFeaturesList />} />
                            <Route path="/bolt-jr/keyfeatures/new" element={<BoltCreateKeyFeature />} />
                            <Route path="/bolt-jr/keyfeatures/:id/edit" element={<BoltEditKeyFeature />} />

                            {/* Features */}
                            <Route path="/bolt-jr/features" element={<BoltFeaturesList />} />
                            <Route path="/bolt-jr/features/new" element={<BoltCreateFeature />} />
                            <Route path="/bolt-jr/features/:id/edit" element={<BoltEditFeature />} />

                            {/* Track Options */}
                            <Route path="/bolt-jr/trackoptions" element={<BoltTrackOptionsList />} />
                            <Route path="/bolt-jr/trackoptions/new" element={<BoltCreateTrackOption />} />
                            <Route path="/bolt-jr/trackoptions/:id/edit" element={<BoltEditTrackOption />} />
                            <Route path="/bolt-jr/trackoptions/tabs" element={<BoltTrackOptionTabs />} />

                            {/* Specifications */}
                            <Route path="/bolt-jr/specifications" element={<BoltSpecificationsList />} />
                            <Route path="/bolt-jr/specifications/new" element={<BoltCreateSpecification />} />
                            <Route path="/bolt-jr/specifications/:id/edit" element={<BoltEditSpecification />} />

                            {/* Controllers */}
                            <Route path="/bolt-jr/controllers" element={<BoltControllersList />} />
                            <Route path="/bolt-jr/controllers/new" element={<BoltCreateController />} />
                            <Route path="/bolt-jr/controllers/:id/edit" element={<BoltEditController />} />

                            {/* Accessories */}
                            <Route path="/bolt-jr/accessories" element={<BoltAccessoriesList />} />
                            <Route path="/bolt-jr/accessories/new" element={<BoltCreateAccessory />} />
                            <Route path="/bolt-jr/accessories/:id/edit" element={<BoltEditAccessory />} />

                            {/* Videos */}
                            <Route path="/bolt-jr/videos" element={<BoltVideosList />} />
                            <Route path="/bolt-jr/videos/new" element={<BoltCreateVideo />} />
                            <Route path="/bolt-jr/videos/:id/edit" element={<BoltEditVideo />} />

                            {/* 404 route */}
                            <Route
                              path="*"
                              element={
                                <div className="p-8 text-center">
                                  <h1 className="text-2xl font-bold">
                                    404 Not Found
                                  </h1>
                                  <p className="mt-2">
                                    The page you are looking for does not exist.
                                  </p>
                                </div>
                              }
                            />
                          </Routes>
                        </Suspense>
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </BrowserRouter>
          </LocaleProvider>
        </ProjectProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
